// [qrprint] QR Code print page — supports 3 modes:
//   'grid'    — grid layout (multiple tables, A4, 3-col)
//   'receipt' — thermal receipt layout (single table, 80mm)
//   'dynamic' — dynamic session QR, receipt layout with expiry badge
import React from "react";
import { useSelector } from "react-redux";
import { QRCodeSVG } from "qrcode.react";
import { encodeQrToken } from "utils/qrToken";

const formatExpiry = (expiresAt) => {
  if (!expiresAt) return ""
  const d = new Date(expiresAt)
  if (isNaN(d)) return ""
  const pad = (n) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// [qrprint] Grid mode — multiple tables, 3-col A4
function GridPrint({ tables, zoneId, zoneName, slug, mobileAppUrl }) {
  return (
    <div id="section-to-print-qr-page">
      <div className="qr-print-grid">
        {tables.map((table) => {
          const token = encodeQrToken({ slug, zoneId, tableId: table.tableId })
          const qrUrl = `${mobileAppUrl}/${slug}/q/${token}`
          return (
            <div key={table.tableId} className="qr-print-card">
              <div className="qr-print-zone-table">
                {zoneName} — {table.tableName}
              </div>
              <QRCodeSVG value={qrUrl} size={130} level="M" includeMargin={false} />
              <div style={{ fontSize: "9pt", color: "#6b7280" }}>Scan to order</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// [qrprint] Receipt mode — single table, thermal 80mm static QR
function ReceiptPrint({ table, zoneId, zoneName, slug, shopName, mobileAppUrl }) {
  const token = encodeQrToken({ slug, zoneId, tableId: table.tableId })
  const qrUrl = `${mobileAppUrl}/${slug}/q/${token}`
  return (
    <div id="section-to-print-qr-receipt">
      <div className="qr-receipt-card">
        <div className="qr-receipt-shop">{shopName || slug}</div>
        <div className="qr-receipt-zone">{zoneName && `${zoneName} — `}{table.tableName}</div>
        <div className="qr-receipt-code">
          <QRCodeSVG value={qrUrl} size={160} level="M" includeMargin={false} />
        </div>
        <div className="qr-receipt-hint">Scan to order</div>
      </div>
    </div>
  )
}

// [qrprint] Dynamic session mode — business info from state.pos.branch, order items from state.bill.data
function DynamicReceiptPrint({ tableName, zoneName, slug, qrUrl, expiresAt }) {
  const billData = useSelector((state) => state.bill.data)
  const posBranch = useSelector((state) => state.pos.branch)

  const expiryLabel = formatExpiry(expiresAt)
  const resolvedUrl = qrUrl || ""

  const fmt = (price) => {
    const num = typeof price === "string" ? parseFloat(price) : (price || 0)
    return isNaN(num) ? "0.00" : num.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // [qrprint] Business info — state.pos.branch has two possible shapes from getBranchInfo response
  const branchDetail = posBranch?.employee_info?.branch || posBranch?.branch_info || {}
  const businessDetail = branchDetail?.business || branchDetail?.business || {}
  const logoUrl = branchDetail?.business?.image_path || ""
  const shopName = branchDetail?.business?.company_name || slug
  const branchName = branchDetail?.branch_name || ""

  // [qrprint] Order info — from state.bill.data if a bill was loaded (may be empty if no active bill)
  const orderCode = billData?.order?.code || ""
  const items = (billData?.billItems || []).map((bi) => ({
    name: bi.orderItem?.product?.name || "-",
    qty: bi.quantity || 1,
    price: bi.price || 0,
  }))
  const subtotal = items.reduce((sum, item) => sum + parseFloat(item.price || 0), 0)

  const now = new Date()
  const pad = (n) => String(n).padStart(2, "0")
  const dateLabel = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`

  return (
    <div className="flex justify-center text-xl w-full leading-[1.8]">
      <div
        id="section-to-print-qr-receipt"
        className="w-[29.49vw] bg-[#FFF] px-[1.70vw] py-[1.31vw] flex flex-col gap-[0.59vw]"
      >

        {/* [qrprint] Header — same as ReceiptPrintPage */}
        <div className="text-center w-full flex flex-col items-center">
          {logoUrl && (
            <img className="object-cover aspect-square w-[7.71vw] h-fit logo-receipt" src={logoUrl} alt="" />
          )}
          <div className="font-bold">{shopName}</div>
          {orderCode && <div>Order: {orderCode}</div>}
        </div>

        {/* [qrprint] Info rows */}
        <div className="flex flex-col gap-[0.39vw]">
          <div className="flex justify-between gap-[0.98vw]">
            <div className="whitespace-nowrap">สาขา:</div>
            <div className="text-right">{branchName || "-"}</div>
          </div>
          <div className="flex justify-between gap-[0.98vw]">
            <div className="whitespace-nowrap">โต๊ะ:</div>
            <div className="text-right">{zoneName ? `${zoneName} / ${tableName}` : tableName}</div>
          </div>
          <div className="flex justify-between gap-[0.98vw]">
            <div className="whitespace-nowrap">เวลา:</div>
            <div className="text-right">{dateLabel}</div>
          </div>
        </div>

        {/* [qrprint] Items — only when bill has items */}
        {items.length > 0 && (
          <>
            <div>
              <div className="border-dashed border-t-[0.04vw] border-b-[0.04vw] border-black py-[0.39vw] flex">
                <div className="w-1/4 text-center font-medium">จำนวน</div>
                <div className="w-1/2 text-center font-medium">รายการ</div>
                <div className="w-1/4 text-right font-medium">หน่วยละ</div>
                <div className="w-1/4 text-right font-medium">รวมเงิน</div>
              </div>
              {items.map((item, i) => (
                <div key={i} className="flex pt-[0.29vw]">
                  <div className="w-1/4 text-center align-top leading-[1.45]">{item.qty}</div>
                  <div className="w-1/2 align-top break-words leading-[1.45]">{item.name}</div>
                  <div className="w-1/4 text-right align-top leading-[1.45]">{fmt(item.price)}</div>
                  <div className="w-1/4 text-right align-top leading-[1.45]">{fmt(item.price)}</div>
                </div>
              ))}
            </div>
            <div className="border-dashed border-t-[0.04vw] border-black h-0 my-[1.28vw]"></div>
            <div className="flex justify-between w-full font-bold">
              <div className="whitespace-nowrap">ยอดรวม</div>
              <div>{fmt(subtotal)}</div>
            </div>
          </>
        )}

        <div className="border-dashed border-t-[0.04vw] border-black h-0 my-[1.28vw]"></div>

        {/* [qrprint] QR code */}
        <div className="flex flex-col items-center gap-[0.59vw]">
          {expiryLabel && <div className="text-gray-400">หมดอายุ {expiryLabel}</div>}
          {resolvedUrl
            ? <QRCodeSVG value={resolvedUrl} size={160} level="M" includeMargin={false} />
            : <div style={{ color: "#ef4444" }}>ไม่สามารถสร้าง QR ได้</div>
          }
          <div className="text-gray-400">สั่งอาหาร</div>
        </div>

      </div>
    </div>
  )
}

export default function QrTablePrintPage({
  mode = "grid",
  // grid props
  tables,
  zoneId,
  zoneName,
  slug,
  // receipt props (single table)
  table,
  shopName,
  // dynamic props
  qrToken,
  qrUrl,
  expiresAt,
  tableName,
}) {
  const mobileAppUrl = (process.env.REACT_APP_MOBILE_APP_URL || "").replace(/\/+$/, "")

  if (mode === "dynamic") {
    return (
      <DynamicReceiptPrint
        tableName={tableName || ""}
        zoneName={zoneName || ""}
        slug={slug}
        qrUrl={qrUrl || (qrToken ? `${mobileAppUrl}/${slug}/q/${qrToken}` : "")}
        expiresAt={expiresAt}
      />
    )
  }

  if (mode === "receipt") {
    if (!table) return null
    return (
      <ReceiptPrint
        table={table}
        zoneId={zoneId}
        zoneName={zoneName}
        slug={slug}
        shopName={shopName}
        mobileAppUrl={mobileAppUrl}
      />
    )
  }

  // default: grid
  if (!tables || tables.length === 0) return null
  return (
    <GridPrint
      tables={tables}
      zoneId={zoneId}
      zoneName={zoneName}
      slug={slug}
      mobileAppUrl={mobileAppUrl}
    />
  )
}
// =====================[QrTablePrintPage]=====================
